import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getCollection } from '@/utils';
import { ATTRIBUTE_COLLECTION } from '@/constants/constants';
import { BSON } from 'realm-web';
import { deleteVariationsByIds } from '@/services';
import {
  createAttribute,
  fetchAttributes,
  updateAttribute,
} from '@/services/Attributes/attributes.service';

export interface Attribute {
  _id: string;
  companyId: string;
  optionName: string;
  sortingOrder: number;
  options: {
    name: string;
    order: number;
  }[];
}

type AttributeInput = Omit<Attribute, '_id'> & {
  _id?: string;
  variantsIds?: string[];
};

export const attributesApiSlice = createApi({
  reducerPath: 'attributesApi',
  tagTypes: ['AttributesApi'],
  baseQuery: fetchBaseQuery(),
  endpoints: (builder) => ({
    fetchAttributes: builder.query<Attribute[], { companyId: string }>({
      queryFn: async ({ companyId }) => {
        try {
          const data = await fetchAttributes(companyId);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: () => [{ type: 'AttributesApi' }],
    }),
    createOrUpdateAttributes: builder.mutation<Attribute, AttributeInput>({
      async queryFn(attributeInput) {
        const { _id, companyId, variantsIds } = attributeInput;
        try {
          let data;
          if (_id) {
            data = await updateAttribute(attributeInput as Attribute, companyId);
          } else {
            data = await createAttribute(attributeInput as Attribute, companyId);
          }
          if (variantsIds) {
            await deleteVariationsByIds(variantsIds);
          }
          return { data: data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: () => [{ type: 'AttributesApi' }],
    }),
    deleteAttributes: builder.mutation({
      async queryFn(body) {
        const { companyId, _id } = body.attribute;
        const attributesColl = getCollection(ATTRIBUTE_COLLECTION);
        try {
          await attributesColl?.deleteOne({
            companyId: new BSON.ObjectID(companyId),
            _id: new BSON.ObjectID(_id),
          });
          if (body.variantsIds) {
            await deleteVariationsByIds(body.variantsIds);
          }
          return { data: _id };
        } catch (error: any) {
          return { error: error.message || 'Unknown error' };
        }
      },
      invalidatesTags: [{ type: 'AttributesApi' }],
    }),
  }),
});

export const {
  useFetchAttributesQuery,
  useDeleteAttributesMutation,
  useCreateOrUpdateAttributesMutation,
} = attributesApiSlice;
